import { useTranslation } from 'react-i18next';
import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import {
  Iconify,
  Label,
  makeConfig,
  OneLineTypography,
  PaginatedTable2,
  Tooltip,
} from '@pflegenavi/web-components';
import { IconButton, Stack, Typography } from '@mui/material';
import {
  useFormatDate,
  useFormatTime,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import {
  TIME_FORMAT,
  YEAR_MONTH_DAY_SHORT_FORMAT,
} from '@pflegenavi/shared/constants';
import { useTheme } from '@mui/material/styles';
import {
  getReceiptBatchFileName,
  getReceiptBatchFileNameDateString,
  sanitizeTenantName,
} from '@pflegenavi/shared/utils';
import type { PaginatedReceiptBatchEntryDto } from '@pflegenavi/service-provider-shared/api-endpoints';
import { ReceiptBatchState } from '@pflegenavi/service-provider-shared/api-endpoints';
import { useNavigate } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { PaymentStatus } from '@pflegenavi/shared/api';
import { useGetBatchReceiptDateString } from '@pflegenavi/shared/frontend';
import type {
  ReceiptBatchFilterKey,
  ReceiptBatchFiltersState,
  ReceiptBatchTab,
} from './useReceiptBatchFilters';
import { useReceiptBatchFilterDefinitions } from './ReceiptBatchFilters';

type ColumnNames =
  | 'nursingHome'
  | 'receiptInfo'
  | 'amount'
  | 'receiptImage'
  | 'status'
  | 'acceptedDate';

interface ServiceProviderReceiptBatchOverviewTableViewProps {
  data: Array<
    PaginatedReceiptBatchEntryDto & {
      receiptDateString: string;
    }
  >;
  totalRowCount: number;
  page: number;
  pageSize: number;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
  onClickReceiptIcon: ({
    id,
    tenant,
    receiptImageIds,
    title,
    isPreview,
  }: {
    id: string;
    tenant: string;
    receiptImageIds: string[] | undefined;
    title: string;
    isPreview?: boolean;
  }) => Promise<void>;

  isLoading: boolean;
  isError: boolean;
  filtersState: ReceiptBatchFiltersState;
}

export interface ServiceProviderReceiptBatchRowModel {
  id: string;
  nursingHomeId: string;
  nursingHomeName: string;
  tenant: string;
  receiptDateString: string;
  receiptCountString: number;
  amountString: string;
  totalAmount: number;
  hasImage: boolean;
  status: ReceiptBatchState;
  statusString: string;
  paymentStatus: 'Unknown' | PaymentStatus;
  amountOpen: number;
  amountCancelled: number;
  amountDeleted: number;
  entryCount: number;
  paidOutEntryCount: number;
  deletedEntryCount: number;
  acceptedDateString?: string;
  lastUpdateDateString: string;
  batchImageIds?: string[];
  autoGenerateReceipts: boolean;
  previewFileName: string;
  onClickReceiptIcon: ({
    id,
    tenant,
    receiptImageIds,
    title,
    isPreview,
  }: {
    id: string;
    tenant: string;
    receiptImageIds: string[] | undefined;
    title: string;
    isPreview?: boolean;
  }) => Promise<void>;
}

export const ServiceProviderReceiptBatchOverviewTableView: FC<
  ServiceProviderReceiptBatchOverviewTableViewProps
> = ({
  data,
  onChangePageSize,
  page,
  pageSize,
  onChangePage,
  totalRowCount,
  onClickReceiptIcon,
  isLoading,
  isError,
  filtersState,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();
  const fTime = useFormatTime();
  const navigate = useNavigate();

  const { getBatchReceiptDateString } = useGetBatchReceiptDateString();

  const filters = useReceiptBatchFilterDefinitions(filtersState);
  const { tab, set } = filtersState;
  const setTab = useCallback(
    (value: ReceiptBatchTab) =>
      set('status', value === 'all' ? undefined : value),
    [set]
  );

  const transformed = useMemo(() => {
    return data.map<ServiceProviderReceiptBatchRowModel>((row) => {
      const paymentStatus = row.payment?.status ?? 'Unknown';

      const getReceiptDateString = () => {
        return getBatchReceiptDateString(
          row.useIndividualDates,
          row.minReceiptDate,
          row.maxReceiptDate,
          row.receiptDate
        );
      };

      const previewFileName = getReceiptBatchFileName(
        getReceiptBatchFileNameDateString(
          row.useIndividualDates,
          row.receiptDate,
          row.minReceiptDate,
          row.maxReceiptDate
        ),
        t('receipts.batch.receipt-batch-generation-file-name')
      );

      const amountPaid = row.payment?.amountPaid ?? 0;
      const amountCancelled = row.payment?.cancelledAmount ?? 0;
      const amountDeleted = row.payment?.deletedAmount ?? 0;
      // Deleted receipts will never be paid, so they are not open either.
      const amountOpen = row.totalAmount - amountPaid - amountDeleted;

      return {
        id: row.id,
        nursingHomeId: row.nursingHomeId,
        nursingHomeName: row.nursingHomeName,
        tenant: row.tenant,
        status: row.state,
        statusString: t(
          `service-provider-app.receipt-batch-overview-table.tabs.${row.state.toLowerCase()}`
        ),
        paymentStatus,
        totalAmount: row.totalAmount,
        amountOpen,
        amountCancelled,
        amountDeleted,
        entryCount: row.entryCount,
        paidOutEntryCount: row.paidOutEntryCount,
        deletedEntryCount: row.payment?.deletedEntryCount ?? 0,
        receiptCountString: t(
          'service-provider-app.receipt-batch-overview-table.receipts-count',
          {
            count: row.entryCount,
          }
        ),
        amountString: fCurrencyCents(row.totalAmount),
        receiptDateString: getReceiptDateString(),
        hasImage: row.receiptImageIds ? row.receiptImageIds.length > 0 : false,
        autoGenerateReceipts: row.generateReceipts,
        acceptedDateString: row.acceptedDate
          ? fDate(row.acceptedDate, YEAR_MONTH_DAY_SHORT_FORMAT)
          : '--',
        lastUpdateDateString:
          fDate(row.updatedOn, YEAR_MONTH_DAY_SHORT_FORMAT) +
          ', ' +
          fTime(row.updatedOn, TIME_FORMAT),
        batchImageIds: row.receiptImageIds,
        previewFileName: previewFileName,
        onClickReceiptIcon: onClickReceiptIcon,
      };
    });
  }, [
    data,
    t,
    fCurrencyCents,
    fDate,
    fTime,
    onClickReceiptIcon,
    getBatchReceiptDateString,
  ]);

  const config = useMemo(
    () =>
      makeConfig<
        ServiceProviderReceiptBatchRowModel,
        ColumnNames,
        ReceiptBatchFilterKey
      >({
        showAll: undefined,
        onClickRow: (row) => {
          navigate(
            spAppMainPages.RECEIPT_BATCH_EDIT.replace(':batchId', row.id)
          );
        },
        emptyRowHeight: 76,
        tabs: {
          selectedItem: tab,
          updateFilterModel: setTab,
          items: [
            {
              value: 'all',
              label: t(
                'service-provider-app.receipt-batch-overview-table.tabs.all'
              ),
              color: 'default',
              count: undefined,
            },
            {
              value: ReceiptBatchState.Accepted,
              label: t(
                'service-provider-app.receipt-batch-overview-table.tabs.accepted'
              ),
              color: 'success',
              count: undefined,
            },
            {
              value: ReceiptBatchState.InReview,
              label: t(
                'service-provider-app.receipt-batch-overview-table.tabs.in_review'
              ),
              color: 'info',
              count: undefined,
            },
            // {
            //   value: ReceiptBatchState.ChangeRequested,
            //   label: t(
            //     'service-provider-app.receipt-batch-overview-table.tabs.change_requested'
            //   ),
            //   color: 'warning',
            //   count: undefined,
            // },
            {
              value: ReceiptBatchState.Draft,
              label: t(
                'service-provider-app.receipt-batch-overview-table.tabs.draft'
              ),
              color: 'default',
              count: undefined,
            },
            // {
            //   value: ReceiptBatchState.Declined,
            //   label: t(
            //     'service-provider-app.receipt-batch-overview-table.tabs.declined'
            //   ),
            //   color: 'error',
            //   count: undefined,
            // },
          ],
        },
        columns: [
          {
            key: 'nursingHome',
            label: t(
              'service-provider-app.receipt-batch-overview-table.columns.nursing-home'
            ),
            align: 'left',
            component: NursingHomeColumn,
          },
          {
            key: 'receiptInfo',
            label: t(
              'service-provider-app.receipt-batch-overview-table.columns.receipts'
            ),
            align: 'left',
            component: ReceiptInfoColumn,
          },
          {
            key: 'amount',
            label: t(
              'service-provider-app.receipt-batch-overview-table.columns.amount'
            ),
            align: 'right',
            component: PaymentAmountColumn,
          },
          {
            key: 'receiptImage',
            label: t(
              'service-provider-app.receipt-batch-overview-table.columns.receiptImage'
            ),
            align: 'center',
            component: ReceiptIconColumn,
          },
          {
            key: 'status',
            label: t(
              'service-provider-app.receipt-batch-overview-table.columns.status'
            ),
            align: 'left',
            component: (props) => (
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'flex-start',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <StatusColumn {...props} />
                <PaymentStatusColumn {...props} />
              </Stack>
            ),
          },
          {
            key: 'acceptedDate',
            label: t(
              'service-provider-app.receipt-batch-overview-table.columns.updatedDate'
            ),
            align: 'left',
            component: ({ row }) => {
              return (
                <Typography variant="subtitle2">
                  {row.lastUpdateDateString}
                </Typography>
              );
            },
          },
        ],
        filters,
        pagination: {
          page,
          pageSize,
          pageSizeOptions: [5, 10, 25, 50],
          total: totalRowCount,
          onPageChange: onChangePage,
          onPageSizeChange: onChangePageSize,
        },
      }),
    [
      filters,
      navigate,
      onChangePage,
      onChangePageSize,
      page,
      pageSize,
      setTab,
      t,
      tab,
      totalRowCount,
    ]
  );

  return (
    <PaginatedTable2
      name="ServiceProviderReceiptBatchOverviewTable"
      config={config}
      rows={transformed}
      isLoading={isLoading}
      isError={isError}
    />
  );
};

const NursingHomeColumn: React.FC<{
  row: ServiceProviderReceiptBatchRowModel;
}> = ({ row }) => {
  return (
    <Stack
      sx={{
        justifyContent: 'center',
        gap: 0.5,
        maxWidth: 180,
      }}
    >
      <OneLineTypography sx={{ fontWeight: 700 }} variant="subtitle2">
        {row.nursingHomeName}
      </OneLineTypography>
      <OneLineTypography variant="subtitle2" color="text.secondary">
        {sanitizeTenantName(row.tenant)}
      </OneLineTypography>
    </Stack>
  );
};

const ReceiptInfoColumn: React.FC<{
  row: ServiceProviderReceiptBatchRowModel;
}> = ({ row }) => {
  return (
    <Stack
      sx={{
        justifyContent: 'center',
        gap: 0.5,
      }}
    >
      <OneLineTypography sx={{ fontWeight: 700 }} variant="subtitle2">
        {row.receiptDateString}
      </OneLineTypography>
      <OneLineTypography variant="subtitle2" color="text.secondary">
        {row.receiptCountString}
      </OneLineTypography>
    </Stack>
  );
};

const StatusColumn: React.FC<{
  row: ServiceProviderReceiptBatchRowModel;
}> = ({ row }) => {
  const color = useMemo(() => {
    switch (row.status) {
      case ReceiptBatchState.Draft:
        return 'default';
      case ReceiptBatchState.InReview:
        return 'info';
      case ReceiptBatchState.ChangeRequested:
        return 'warning';
      case ReceiptBatchState.Declined:
        return 'error';
      case ReceiptBatchState.Accepted:
        return 'success';
      default:
        return 'default';
    }
  }, [row.status]);
  return (
    <Label variant="ghost" color={color}>
      {row.statusString}
    </Label>
  );
};

const PaymentAmountColumn: React.FC<{
  row: ServiceProviderReceiptBatchRowModel;
}> = ({ row }) => {
  const { fCurrencyCents } = useFormatting();
  const { t } = useTranslation();
  const theme = useTheme();

  // Show breakdown only for Accepted batches with payment data (MangoPay)
  // Stripe tenants (paymentStatus === 'Unknown') never show breakdown
  const shouldShowBreakdown =
    row.status === ReceiptBatchState.Accepted &&
    row.paymentStatus !== 'Unknown';

  if (!shouldShowBreakdown) {
    return <Typography variant="subtitle2">{row.amountString}</Typography>;
  }

  const openText = t(
    'service-provider-app.receipt-batch-overview-table.payment.open'
  );
  const cancelledText = t(
    'service-provider-app.receipt-batch-overview-table.payment.cancelled'
  );
  const deletedText = t(
    'service-provider-app.receipt-batch-overview-table.payment.deleted'
  );

  return (
    <Stack
      direction="column"
      spacing={0.3}
      sx={{
        alignItems: 'flex-end',
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontSize: '0.875rem', fontWeight: 500 }}
      >
        {fCurrencyCents(row.totalAmount)}
      </Typography>
      {row.amountOpen > 0 && (
        <Typography
          variant="body2"
          sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
        >
          {fCurrencyCents(row.amountOpen)} {openText}
        </Typography>
      )}
      {row.amountCancelled > 0 && (
        <Typography
          variant="body2"
          sx={{ fontSize: '0.875rem', color: theme.palette.error.main }}
        >
          {fCurrencyCents(row.amountCancelled)} {cancelledText}
        </Typography>
      )}
      {row.amountDeleted > 0 && (
        <Typography
          variant="body2"
          sx={{ fontSize: '0.875rem', color: theme.palette.error.main }}
        >
          {fCurrencyCents(row.amountDeleted)} {deletedText}
        </Typography>
      )}
    </Stack>
  );
};

const PaymentStatusColumn: React.FC<{
  row: ServiceProviderReceiptBatchRowModel;
}> = ({ row }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const color = useMemo(() => {
    switch (row.paymentStatus) {
      case PaymentStatus.Pending:
        return theme.palette.warning.main;
      case PaymentStatus.Failed:
      case PaymentStatus.Disputed:
        return theme.palette.error.main;
      default:
        return theme.palette.warning.main; // Default warning for unpaid accepted batches
    }
  }, [row.paymentStatus, theme]);

  // All entries paid out to service provider - show green check.
  // Entries whose receipt was deleted never get paid out, so they don't block it.
  const isFullyPaidOut =
    row.entryCount > 0 &&
    row.paidOutEntryCount + row.deletedEntryCount >= row.entryCount &&
    row.status === ReceiptBatchState.Accepted;

  if (isFullyPaidOut) {
    return (
      <Tooltip
        title={t('service-provider-app.receipt-batch-overview-table.paid-out')}
      >
        <Stack
          sx={{
            justifyContent: 'center',
            flexDirection: 'row',
            alignContent: 'center',
          }}
        >
          <Iconify
            icon="mdi:check-circle"
            color={theme.palette.success.main}
            width={18}
            height={18}
          />
        </Stack>
      </Tooltip>
    );
  }

  // Don't show payment icon if:
  // - No payment data (Stripe tenants or MangoPay without payment initiated)
  // - Fully paid by nursing home
  // - Not Accepted
  if (
    row.paymentStatus === 'Unknown' ||
    row.amountOpen === 0 ||
    row.status !== ReceiptBatchState.Accepted
  ) {
    return null;
  }

  // Show warning for Accepted batches with outstanding amounts
  return (
    <Tooltip
      title={t(
        `receipts.batch.overview-table.payment-status.${row.paymentStatus}`
      )}
    >
      <Stack
        sx={{
          justifyContent: 'center',
          flexDirection: 'row',
          alignContent: 'center',
        }}
      >
        <Iconify
          icon="majesticons:euro-circle"
          color={color}
          width={18}
          height={18}
        />
      </Stack>
    </Tooltip>
  );
};

const ReceiptIconColumn: React.FC<{
  row: ServiceProviderReceiptBatchRowModel;
}> = ({ row }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isPreview = useMemo(() => {
    if (row.status === ReceiptBatchState.Draft) {
      return row.autoGenerateReceipts;
    }
    if (row.hasImage) {
      return false;
    }
    return false;
  }, [row.autoGenerateReceipts, row.hasImage, row.status]);

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      await row.onClickReceiptIcon({
        id: row.id,
        tenant: row.tenant,
        receiptImageIds: row.hasImage ? row.batchImageIds : undefined,
        title: isPreview
          ? row.previewFileName
          : `${row.nursingHomeName} - ${row.receiptDateString} - ${row.amountString}`,
        isPreview,
      });
    },
    [isPreview, row]
  );

  const { tooltip, icon, color } = useMemo(() => {
    const tooltip =
      row.hasImage || isPreview
        ? t('receipts.show-receipt-image')
        : t('tooltip.without-image');
    const icon =
      row.hasImage || isPreview
        ? 'mdi:receipt-text-check'
        : 'mdi:receipt-text-remove';
    const color = isPreview
      ? theme.palette.info.main
      : row.hasImage
      ? theme.palette.success.main
      : theme.palette.grey['400'];
    return {
      tooltip,
      icon,
      color,
    };
  }, [
    isPreview,
    row.hasImage,
    t,
    theme.palette.grey,
    theme.palette.info.main,
    theme.palette.success.main,
  ]);
  return (
    <Tooltip title={tooltip}>
      <IconButton onClick={handleClick}>
        <Iconify icon={icon} color={color} fontSize={28} />
      </IconButton>
    </Tooltip>
  );
};
